import React from 'react'
import { useListBoxSection } from 'react-aria'
import type { ListState } from 'react-stately'
import type { Node } from '@react-types/shared'
import { Box, Flex, Text } from '../../../core-components'
import { SelectItemInternal } from '../select-item/SelectItem'
import { DynamicItem, DynamicSectionItem } from '../base-select/dynamicItemUtils'
import { VirtualItemWrapper, VirtualItemWrapperFromContext } from '../select-list/virtualScroll'
import { useSelectItemsData } from '../base-select/SelectItemContext'

type Props = {
  section: Node<DynamicSectionItem>
  state: ListState<DynamicItem>
  useVirtualScroll?: boolean
}

export const SelectSectionInternal = ({ section, state, useVirtualScroll }: Props) => {
  const items = [...(state.collection.getChildren?.(section.key) ?? [])]
  const itemsData = useSelectItemsData()
  const isSectionVisible = items.some(node => !itemsData[node.key].isHidden)
  const { itemProps, headingProps, groupProps } = useListBoxSection({
    heading: section.rendered,
    'aria-label': section['aria-label'],
  })

  const title = (
    <Flex px={5} pt='26px' pb='10px'>
      <Text color='text/action-inform' textStyle='heading-xs' {...headingProps}>
        {section.rendered}
      </Text>
    </Flex>
  )
  return (
    <Box as='li' {...itemProps} display={isSectionVisible ? 'block' : 'none'}>
      {section.rendered &&
        (useVirtualScroll ? (
          <VirtualItemWrapper itemKey={section.key} selectionManager={state.selectionManager}>
            {title}
          </VirtualItemWrapper>
        ) : (
          title
        ))}
      <Box as='ul' {...groupProps}>
        {items.map(node => {
          if (useVirtualScroll) {
            return (
              <VirtualItemWrapperFromContext
                key={node.key}
                itemKey={node.key}
                selectionManager={state.selectionManager}
              >
                <SelectItemInternal
                  key={node.key}
                  item={node}
                  state={state}
                  variant={
                    state.selectionManager.selectionMode === 'multiple' ? 'multi' : 'regular'
                  }
                />
              </VirtualItemWrapperFromContext>
            )
          }
          return (
            <SelectItemInternal
              key={node.key}
              item={node}
              state={state}
              variant={state.selectionManager.selectionMode === 'multiple' ? 'multi' : 'regular'}
            />
          )
        })}
      </Box>
    </Box>
  )
}

type PublicProps = {
  children: React.ReactNode
  'data-id'?: string
  'data-test-id'?: string
  title: string
}

export const SelectSectionPublic = SelectSectionInternal as unknown as React.FC<PublicProps>
